<template>
  <div>
    <div
      v-if="!audit"
    >
      <div
        class="d-flex align-items-center flex-wrap"
      >
        <GroupOrdersDropdown
          class="mr-3 mb-1"
          :group-key.sync="groupKey"
          :single-order-schema="singleOrderSchema"
        />
        <CalendarDropdown
          v-if="!singleOrderSchema"
          :highlighted.sync="highlighted"
          :available-days="availableDays"
          :day-status="dayStatus"
          :dates.sync="dates"
          :resolution="catalogDetails.orderSchema"
          :init-at="initAt"
          :month-mode="catalogDetails.orderSchema === 'Monthly'"
          class="mr-3 mb-1"
          @title="selectTitle = $event"
        />
        <PayersFilterDropdown
          class="mr-3 mb-1"
          @selected="selectedPayers = $event"
        />
        <div
          v-if="!pendingDownload"
          v-tippy
          class="secondary-icon ml-3"
          :content="$t('shop.downloadReport')"
          @click="download"
        >
          <i class="fas fa-download" />
        </div>
        <div v-else>
          <Loader />
        </div>
        <div
          v-tippy
          class="secondary-icon smaller-icon ml-2"
          :content="$t('bill.changeHistory')"
          @click="audit = true"
        >
          <i class="fas fa-clock-rotate-left" />
        </div>
        <RouterLink
          v-if="catalogDetails.orderSchema === 'Daily'"
          :to="`/payee/${payeeId}/order/today?catalogId=${catalogDetails.id}`"
          target="_blank"
        >
          <div
            v-tippy
            class="secondary-icon smaller-icon ml-2"
            :content="$t('shop.screenForDelivery')"
          >
            <i class="fas fa-hand-holding-box" />
          </div>
        </RouterLink>
        <RouterLink
          v-if="catalogDetails.orderSchema === 'Daily' && catalogDetails.productCategory === 'Catering'"
          :to="`/payee/${payeeId}/shop/${catalogDetails.id}/kitchen`"
          target="_blank"
        >
          <div
            v-tippy
            class="secondary-icon smaller-icon ml-2"
            :content="$t('shop.screenForKitchen')"
          >
            <i class="fas fa-tv" />
          </div>
        </RouterLink>
        <div
          class="flex-grow-1 d-flex align-items-center justify-content-end"
        >
          <div>
            <button
              class="btn btn-sm btn-primary"
              data-test="toggle-add-client"
              @click="showOrderForm"
            >
              <i class="fas fa-plus pr-1" />
              {{ $t('shop.addOrders') }}
            </button>
          </div>
        </div>
      </div>
      <hr class="my-2">
      <div class="d-flex mb-1">
        <ReportCheckbox
          v-if="catalogDetails.orderSchema === 'Single'
            || catalogDetails.productCategory !== 'Catering'"
          v-model="showReserveQueue"
          class="mr-3"
          :text="$t('shop.showReserveQueue')"
        />

        <ReportCheckbox
          v-if="!showReserveQueue"
          v-model="showUnpaid"
          class="mr-3"
          :disabled="catalogDetails.orderSchema === 'Single'"
          :text="$t('shop.showUnpaid')"
        />

        <OrderStatusDropdown
          v-if="!showReserveQueue"
          :statuses="catalogDetails.orderStatuses"
          class="mr-3"
          :selected.sync="selectedOrderStatus"
          @edit="orderStatusForm = true"
        />
        <ReportCheckbox
          v-if="groupKey === 'productSets' && !showReserveQueue"
          v-model="showAllListings"
          class="mr-3"
          :text="$t('shop.showAllListings')"
        />
        <ReportCheckbox
          v-if="groupKey === 'productSets' && !showReserveQueue && hasMinCountFields"
          v-model="showBelowMinimum"
          class="mr-3"
          :text="$t('shop.showBelowMinimum')"
        />
        <!-- <MonthDropdown
          :title="$t('shop.orderedDate')"
          class="mr-3"
          small
          :selected.sync="ordered"
        /> -->
        <LayoutConfig
          v-if="!showReserveQueue"
          :layout="layout"
          class="mr-3"
          :other-columns="extraColumns"
          :save="saveLayout"
        />
      </div>
    </div>

    <BModal
      v-model="orderStatusForm"
      hide-footer
      size="xl"
      hide-header
    >
      <EditOrderStatuses
        :catalog-id="catalogDetails.id"
        :statuses="catalogDetails.orderStatuses"
        @close="orderStatusForm = false"
      />
    </BModal>

    <ColumnEdit
      :edit="!!edited || editedItems.length > 0"
      :focused="false"
      class="h-100"
    >
      <template v-if="!audit">
        <OrdersList
          v-if="payers"
          ref="list"
          class="flex-grow-1"
          :dates="dates"
          :layout="showReserveQueue ? reserveLayout : layout"
          :reserve-queue="showReserveQueue"
          :aggregated-orders="aggregatedOrders"
          :pending="pending"
          :product-sets="productSets"
          :group-by="groupKey"
          :filter="filter"
          :payers="payers"
          :edited-id="edited ? edited.id : null"
          @refresh="request(true)"
          @select="selectOrder"
          @bulkEdit="editedItems = $event; edited = null"
        />
      </template>

      <OrdersAudit
        v-else
        :catalog-id="catalogDetails.id"
        @close="audit = false"
      />
      <template #edit>
        <div
          v-if="edited || editedItems.length > 0"
          class="page-cart order-details"
        >
          <BulkOrderDetails
            v-if="editedItems.length > 0"
            :orders="editedItems"
            @update="updateItem"
            @close="editedItems = []"
            @refresh="request(true)"
          />
          <SingleOrderDetails
            v-if="edited"
            :key="edited.id"
            :order="edited"
            @update="updateItem"
            @close="edited = null; editedItems = []"
            @refresh="request(true)"
          />
        </div>
      </template>
    </ColumnEdit>
  </div>
</template>

<script>
import ColumnEdit from '@/components/layout/ColumnEdit';
import PayersFilterDropdown from '@/components/payers/PayersFilterDropdown';
import ReportCheckbox from '@/components/reports/ReportCheckbox';
import OrdersAudit from '@/components/shop/audit/OrdersAudit';
import EditOrderStatuses from '@/components/shop/catalog/EditOrderStatuses';
import CalendarDropdown from '@/components/shop/report/CalendarDropdown';
import GroupOrdersDropdown from '@/components/shop/report/GroupOrdersDropdown';
import OrderStatusDropdown from '@/components/shop/report/table//OrderStatusDropdown';
import LayoutConfig from '@/components/shop/report/table/LayoutConfig';
import OrdersList from '@/components/shop/report/table/OrdersList';
import getFileName from '@/utils/file-name';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import BulkOrderDetails from './order/BulkOrderDetails';
import { extraColumns } from './order/columns';
import SingleOrderDetails from './order/SingleOrderDetails';

const showUnpaidStorageKey = 'shop_showUnpaid';
const groupByStorageKey = 'shop_groupBy';

export default {
  data: () => ({
    dates: null,
    audit: false,
    focus: false,
    orders: {
      orders: [],
      productSets: [],
    },
    showReserveQueue: false,
    calendarOrders: [],
    orderGroups: null,
    selectTitle: '',
    showUnpaid: true,
    groupKey: 'payers',
    highlighted: [],
    productSets: {},
    selectedPayers: [],
    payers: null,
    pendingDownload: false,
    pending: false,
    showAllListings: false,
    showBelowMinimum: false,
    previousFrom: null,
    haveLayout: false,
    layout: [],
    edited: null,
    editedItems: [],
    selected: null,
    ordered: null,
    selectedOrderStatus: null,
    orderStatusForm: false,
    initAt: null,
    requestId: 0,
  }),
  components: {
    OrdersList,
    ColumnEdit,
    CalendarDropdown,
    ReportCheckbox,
    LayoutConfig,
    BulkOrderDetails,
    EditOrderStatuses,
    SingleOrderDetails,
    GroupOrdersDropdown,
    PayersFilterDropdown,
    OrderStatusDropdown,
    OrdersAudit,
  },
  computed: {
    ...mapGetters(['payeeId', 'payee']),
    ...mapGetters('shop', [
      'catalogDetails',
      'singleOrderSchema',
    ]),
    reserveLayout() {
      return this.layout.filter((x) =>
        x.key !== 'delivered'
        && x.key !== 'count'
        && x.key !== 'price'
        && x.key !== 'count'
        && x.key !== 'discount'
        && x.key !== 'comment'
        && x.key !== 'paid')
        .concat([{
          key: 'position',
          type: 'position',
          name: 'Pozycja w kolejce',
          icon: 'far fa-arrow-up-short-wide',
          width: 140,
        }]);
    },
    extraColumns() {
      const base = extraColumns
        .concat(this.catalogDetails.subCatalogs
          .filter((x) => !x.subCatalogId.startsWith('#'))
          .map((c) => ({
            key: `subcatalog:${c.subCatalogId}`,
            type: 'subcatalog',
            name: c.name,
          })))
        .filter((x) => x.key !== 'month' || this.catalogDetails.orderSchema === 'Monthly')
        .filter((x) => x.key !== 'day' || this.catalogDetails.orderSchema === 'Daily')
        .filter((x) => x.key !== 'timetable' || this.catalogDetails.productCategory === 'Courses');

      if (this.groupKey !== 'productSets') return base;

      const allListingFields = [
        ...(this.catalogDetails.listingFields || []),
        ...this.catalogDetails.subCatalogs.flatMap((sc) => sc.listingFields || []),
      ];

      const fieldColumns = allListingFields.map((f) => ({
        key: `listing-field:${f.fieldId}`,
        type: 'listing-field',
        name: f.name,
        icon: 'far fa-tag',
        width: 150,
      }));

      const assignmentColumns = this.catalogDetails.subCatalogs
        .filter((sc) => !sc.subCatalogId.startsWith('#'))
        .map((sc) => ({
          key: `listing-assignment:${sc.subCatalogId}`,
          type: 'listing-assignment',
          name: sc.name,
          icon: 'far fa-sitemap',
          width: 150,
        }));

      return [...base, ...fieldColumns, ...assignmentColumns];
    },
    hasMinCountFields() {
      return (this.catalogDetails.listingFields || []).some((f) => f.type === 'MinCount');
    },
    availableDays() {
      return Object.keys(this.dayStatus);
    },
    payersDictionary() {
      return Object.freeze(this.payers.reduce((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      }, {}));
    },
    aggregatedOrders() {
      if (this.groupKey === 'day') {
        return this.calendarOrders
          .filter((x) => this.highlighted.length === 0 || this.highlighted.includes(x.group))
          .map((o) => ({
            ...o,
            id: o.group,
            totalPrice: o.totalValue,
            currency: 'pln',
            formattedDay: moment(o.group, 'YYYY-MM-DD').format('DD/MM'),
            formattedMonth: moment(o.group, 'YYYY-MM-DD').format('MM/YYYY'),
          }))
          .sort((a, b) => moment(a.group, 'YYYY-MM-DD').diff(moment(b.group, 'YYYY-MM-DD')));
      }

      if (this.orderGroups == null) return [];

      if (this.groupKey === 'payers') {
        return this.orderGroups.map((o) => ({
          ...o,
          id: o.group,
          totalPrice: o.totalValue,
          currency: 'pln',
          payerId: o.group,
          payer: this.payersDictionary[o.group],
        }))
          .sort((a, b) => a.payer?.name.localeCompare(b.payer?.name));
      }
      if (this.groupKey === 'productSets') {
        const fromOrders = this.orderGroups.map((o) => ({
          ...o,
          id: o.group,
          listingName: o.groupName || this.productSets[o.group]?.name || 'Nie znaleziono nazwy',
          totalPrice: o.totalValue,
          currency: 'pln',
          productSetId: o.group,
          listing: this.productSets[o.group] || null,
        }));

        let result = fromOrders;

        if (this.showAllListings) {
          const orderIds = new Set(fromOrders.map((x) => x.id));
          const missingListings = Object.values(this.productSets)
            .filter((ps) => !orderIds.has(ps.id))
            .map((ps) => ({
              id: ps.id,
              group: ps.id,
              listingName: ps.name,
              totalPrice: 0,
              totalValue: 0,
              totalCount: 0,
              currency: 'pln',
              productSetId: ps.id,
              listing: ps,
            }));

          result = [...fromOrders, ...missingListings];
        }

        if (this.showBelowMinimum) {
          const minCountFields = (this.catalogDetails.listingFields || [])
            .filter((f) => f.type === 'MinCount');
          result = result.filter((o) => minCountFields.some((f) => {
            const minValue = Number.parseInt(o.listing?.fields?.[f.fieldId], 10);
            return !Number.isNaN(minValue) && minValue > 0 && (o.totalCount || 0) < minValue;
          }));
        }

        return result.sort((a, b) => (a.listingName || '').localeCompare(b.listingName || ''));
      }

      return [];
    },
    calendarRange() {
      if (!this.dates) return null;

      if (this.catalogDetails.orderSchema !== 'Monthly') return this.dates;

      const year = moment(this.dates.from).year();

      return {
        from: `${year}-01-01`,
        to: `${year + 1}-01-01`,
      };
    },
    dayStatus() {
      return this.calendarOrders.reduce((acc, curr) => {
        acc[curr.group] = {
          paid: curr.paidCount,
          waiting: curr.totalCount - curr.paidCount,
        };

        return acc;
      }, {});
    },
    everyday() {
      if (!this.dates) return [moment().format('YYYY-MM-DD')];
      const curr = moment(this.dates.from);
      const end = moment(this.dates.to);
      const result = [];

      while (curr.isBefore(end)) {
        result.push(curr.format('YYYY-MM-DD'));
        curr.add(1, 'day');
      }

      return result;
    },
    reportDays() {
      return this.highlighted.length === 0
        ? this.everyday
        : this.highlighted;
    },
    filter() {
      return {
        days: this.highlighted,
        payerIds: this.selectedPayers.map((x) => x.id),
        orderStatusId: this.selectedOrderStatus,
        showUnpaid: this.showUnpaid,
        groupBy: this.groupKey,
        showReserveQueue: this.showReserveQueue,
      };
    },
  },
  watch: {
    dates({ from, to }) {
      if (this.debounceRequestDates) {
        clearTimeout(this.debounceRequestDates);
      }

      const isYearScope = moment(to).diff(moment(from), 'months') > 1;

      this.$router.replace({
        query: {
          ...this.$route.query,
          scope: from,
          scopeAll: isYearScope ? '1' : undefined,
        },
      }).catch(() => {});

      this.debounceRequestDates = setTimeout(() => {
        this.request();
        this.refreshCalendarOrders();
      }, 300);
    },
    selectedPayers() {
      if (this.debounceRequestDates) {
        clearTimeout(this.debounceRequestDates);
      }

      this.debounceRequestDates = setTimeout(() => {
        this.refreshCalendarOrders();
      }, 300);
    },
    filter() {
      this.pending = true;
      this.orderGroups = null;
      this.debounceRefreshAggregatedOrders();
    },
    showUnpaid(v) {
      localStorage.setItem(showUnpaidStorageKey, v);
    },
    groupKey(v) {
      localStorage.setItem(`${groupByStorageKey}:${this.catalogDetails.id}`, v);
    },
  },
  methods: {
    ...mapActions(['getPayers']),
    ...mapActions('shop', [
      'getOrders',
      'getAggregatedOrders',
      'getOrdersReport',
      'setCatalogOrdersLayout',
      'getProductSets',
      'getCatalogListings',
    ]),
    getInitialMonthlyScope() {
      const scopeDate = this.$route.query?.scope || moment().format('YYYY-MM-DD');
      const monthStart = moment(scopeDate).startOf('month');

      return {
        from: monthStart.format('YYYY-MM-DD'),
        to: monthStart.clone().add(1, 'month').format('YYYY-MM-DD'),
      };
    },
    saveLayout(layout) {
      const validExtraKeys = new Set(this.extraColumns.map((x) => x.key));
      const validLayout = layout.filter((col) => {
        if (col.type === 'default' || col.type === 'property' || col.type === 'group') return true;
        return validExtraKeys.has(col.key);
      });

      return this.setCatalogOrdersLayout({
        params: {
          catalogId: this.catalogDetails.id,
        },
        data: {
          layout: JSON.stringify(validLayout),
        },
      })
        .then(() => {
          this.layout = validLayout;
        });
    },
    updateItem(order) {
      if (this.$refs.list) {
        this.$refs.list.updateItem(order);
      }

      if (this.edited && this.edited.id === order.id) {
        this.edited.orderStatusId = order.orderStatusId;
        this.edited.isPaid = order.isPaid;
        this.edited.delivered = order.delivered ? 1 : 0;
        this.edited.unitPrice = order.unitPrice;
        this.edited.count = Number.parseInt(order.count, 10);
        this.edited.comment = order.comment;
        this.edited.discountName = order.discountName;
      }
    },
    showOrderForm() {
      this.$router.push(`/payee/${this.payeeId}/shop/${this.catalogDetails.id}/order`);
    },
    selectOrder(o) {
      if (this.edited && this.edited.id === o.id) {
        this.edited = null;
      } else {
        this.edited = o;
        this.editedItems = [];
      }
    },
    initLayout() {
      this.layout = [
        ...(this.catalogDetails.orderSchema === 'Daily' ? [{ key: 'day', type: 'default' }] : []),
        ...(this.catalogDetails.orderSchema === 'Monthly' ? [{ key: 'month', type: 'default' }] : []),
        { key: 'name', type: 'default' },
        { key: 'code', type: 'default' },
        { key: 'productSetName', type: 'default' },
        { key: 'space', type: 'default' },
        { key: 'paid', type: 'default' },
        { key: 'delivered', type: 'default' },
        { key: 'comment', type: 'default' },
        { key: 'created', type: 'default' },
        { key: 'count', type: 'default' },
        { key: 'price', type: 'default' },
      ];

      try {
        if (this.catalogDetails.ordersLayout) {
          this.layout = JSON.parse(this.catalogDetails.ordersLayout);
        }
      } catch {
        //
      }

      this.haveLayout = true;
      this.requestPayers();
    },
    refreshLayout() {
      this.initLayout();
      this.refresh();
    },
    download() {
      this.pendingDownload = true;

      this.getOrdersReport({
        data: {
          catalogId: this.catalogDetails.id,
          days: this.reportDays,
          showUnpaid: this.showUnpaid,
          payerIds: this.selectedPayers.map((x) => x.id),
          properties: (this.showReserveQueue ? this.reserveLayout : this.layout).map((x) => x.key),
          reserveQueue: this.showReserveQueue,
        },
      })
        .then(({ data, headers }) => {
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;

          link.setAttribute('download', getFileName(headers));
          document.body.appendChild(link);
          link.click();
        })
        .finally(() => {
          this.pendingDownload = false;
        });
    },
    request(refresh) {
      if (!this.dates && !this.singleOrderSchema) return;

      if (!refresh) {
        this.pending = true;
        this.calendarOrders = [];
        this.orderGroups = [];
      }

      this.debounceRefreshAggregatedOrders();
    },
    refreshCalendarOrders() {
      if (!this.calendarRange) return;
      this.getAggregatedOrders({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
            from: this.calendarRange.from,
            to: this.calendarRange.to,
          },
        },
        data: {
          payerIds: this.selectedPayers.map((x) => x.id),
          showUnpaid: true,
          groupBy: 'day',
        },
      })
        .then(({ data }) => {
          this.calendarOrders = data;
        });
    },
    debounceRefreshAggregatedOrders() {
      if (this.debounceRequest) {
        clearTimeout(this.debounceRequest);
      }

      this.debounceRequest = setTimeout(() => {
        this.refreshAggregatedOrders();
      }, 500);
    },
    refreshAggregatedOrders() {
      if (this.groupKey === 'none') {
        this.pending = false;
        return;
      }

      if (!this.singleOrderSchema && !this.dates) {
        this.pending = false;
        return;
      }

      this.requestId += 1;
      this.getAggregatedOrders({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
            from: this.singleOrderSchema ? undefined : this.dates.from,
            to: this.singleOrderSchema ? undefined : this.dates.to,
            reserveQueue: this.showReserveQueue,
          },
          requestId: this.requestId,
        },
        data: {
          payerIds: this.selectedPayers.map((x) => x.id),
          orderStatusId: this.selectedOrderStatus,
          showUnpaid: this.showUnpaid,
          groupBy: this.groupKey,
          days: this.highlighted,
        },
      })
        .then(({ data, config }) => {
          if (config.params.requestId === this.requestId) {
            this.orderGroups = data;
          }
        })
        .finally(() => {
          this.pending = false;
        });
    },
    requestPayers() {
      if (!this.haveLayout) return;

      const withProperty = this.layout.filter((x) => x.type === 'property').map((x) => x.key);
      const withGroup = this.layout.filter((x) => x.type === 'group').map((x) => x.key);

      this.getPayers({
        params: {
          query: {
            skip: 0,
            take: -1,
            withProperty,
            withGroup,
          },
        },
      })
        .then(({ data }) => {
          this.payers = Object.freeze(data);
        });
    },
    requestProductSets() {
      if (this.catalogDetails.id === '00000000-0000-0000-0000-000000000000') {
        this.getProductSets()
          .then(({ data }) => {
            data.forEach((ps) => {
              this.$set(this.productSets, ps.id, ps);
            });
          });
      } else {
        this.getCatalogListings({
          params: {
            catalogId: this.catalogDetails.id,
            query: {
              withCalendar: true,
            },
          },
        })
          .then(({ data }) => {
            data.forEach((ps) => {
              this.$set(this.productSets, ps.id, ps);
            });
          });
      }
    },
  },
  created() {
    if (this.$route.query?.scope) {
      this.initAt = this.$route.query.scope;
    }

    // For Monthly schema, initialize with the whole year, unless a single month was in scope
    if (this.catalogDetails.orderSchema === 'Monthly') {
      if (this.$route.query?.scope && !this.$route.query?.scopeAll) {
        const scope = this.getInitialMonthlyScope();
        this.dates = scope;
        this.highlighted = [scope.from];
      } else {
        const year = moment(this.$route.query?.scope || undefined).year();
        this.dates = {
          from: `${year}-01-01`,
          to: `${year + 1}-01-01`,
        };
        this.highlighted = [];
      }
    }

    const storedShowUnpaid = localStorage.getItem(showUnpaidStorageKey);
    this.showUnpaid = storedShowUnpaid === null
      ? this.catalogDetails.orderSchema === 'Monthly'
      : storedShowUnpaid === 'true';
    this.groupKey = localStorage.getItem(`${groupByStorageKey}:${this.catalogDetails.id}`) || 'payers';

    this.pending = true;
    this.initLayout();
    this.requestPayers();
    this.requestProductSets();
    this.debounceRefreshAggregatedOrders();

    this.$emit('page', 'orders');
  },
};
</script>

<style lang="scss">
  .smaller-icon {
    font-size: 0.85rem;
  }

  .order-details {
    height: calc(100vh - 310px);
    overflow: auto;
  }
</style>
